import { Shield, BookOpen, Network, Terminal, GraduationCap, Briefcase, Search, Building2, Users, ArrowRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import whitehatLogo from "../assets/whitehat-logo.webp";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import TerminalText from "../components/TerminalText";
import usePageTracking from "@/hooks/usePageTracking";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import { CommunityLink, defaultLinks, defaultDivisionLinks, getIconComponent } from "../data/communityLinks";
import TestimonialRotator from "../components/TestimonialRotator";


const MatrixRain = lazy(() => import("../components/MatrixRain"));

interface WhoCanJoinCard {
  title: string;
  description: string;
}

const parseWhoCanJoinContent = (raw: string): WhoCanJoinCard[] => {
  const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
  return lines.map((line) => {
    const [titlePart, ...rest] = line.split("|").map((p) => p.trim());
    const description = rest.join("|").trim();
    const cleanTitle = titlePart.replace(/^[^\p{L}\p{N}]+/u, "").trim();
    return {
      title: cleanTitle || titlePart,
      description: description || cleanTitle || titlePart,
    };
  });
};


type WhyJoinCard = { title: string; description: string };

const parseWhyJoinContent = (raw: string): WhyJoinCard[] => {
  const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
  const pipeParsed = lines
    .map((line) => {
      const [title, ...rest] = line.split("|").map((p) => p.trim());
      const description = rest.join("|").trim();
      if (!title) return null;
      return { title, description: description || "" };
    })
    .filter((x): x is WhyJoinCard => !!x);
  if (pipeParsed.some((x) => x.description)) return pipeParsed;
  return lines.map((title) => ({ title, description: "" }));
};

const Index = () => {
  const whyJoinRef = useRef<HTMLDivElement>(null);
  const [underConstruction, setUnderConstruction] = useState(false);
  const [communityLinks, setCommunityLinks] = useState<CommunityLink[]>(defaultLinks);
  const [divisionLinks, setDivisionLinks] = useState<CommunityLink[]>(defaultDivisionLinks);
  const [linksLoaded, setLinksLoaded] = useState(true);

  const [siteTitle, setSiteTitle] = useState("WhiteHat Hub");
  const [tagline, setTagline] = useState("");
  const [mainMessage, setMainMessage] = useState(
    "Join our vibrant cybersecurity community — whether you're a learner eager to explore or a contributor ready to share your expertise, there's a place for you at WhiteHat Hub."
  );
  const [whoCanJoinTitle, setWhoCanJoinTitle] = useState("Who Can Join WhiteHat Hub?");
  const [whoCanJoinIntro, setWhoCanJoinIntro] = useState("WhiteHat Hub is open to anyone passionate about cybersecurity and ethical hacking, including:");
  const [whoCanJoinContent, setWhoCanJoinContent] = useState("");
  const [whyJoinTitle, setWhyJoinTitle] = useState("Why join WhiteHat Hub?");
  const [whyJoinContent, setWhyJoinContent] = useState(
    "Share Knowledge | Share your expertise, contribute valuable insights, and help others grow in their cybersecurity journey.\nNetwork & Connect | Connect with experienced professionals, founders, and cybersecurity enthusiasts across the ecosystem.\nClear Your Doubts | Get your questions answered and learn directly from experienced cybersecurity professionals."
  );

  const whoCanJoinCards = useMemo(() => parseWhoCanJoinContent(whoCanJoinContent), [whoCanJoinContent]);
  const whyJoinCards = useMemo(() => parseWhyJoinContent(whyJoinContent).slice(0, 6), [whyJoinContent]);

  usePageTracking();
  useRevealOnScroll([linksLoaded, whoCanJoinCards.length, whyJoinCards.length]);

  useEffect(() => {
    const fetchData = async () => {
      const [constructionRes, linksRes, contentRes] = await Promise.all([
        supabase.from("site_content").select("value").eq("key", "under_construction").single(),
        supabase.from("community_links").select("*").eq("is_active", true).order("sort_order"),
        supabase
          .from("site_content")
          .select("key,value")
          .in("key", ["site_title", "tagline", "main_message", "why_join_title", "why_join_content", "who_can_join_title", "who_can_join_intro", "who_can_join_content"]),
      ]);

      if (constructionRes.data) setUnderConstruction(constructionRes.data.value === "true");
      const all = (linksRes.data as CommunityLink[] | null) ?? [];
      const social = all.filter((l) => (l.category ?? "social") === "social");
      const divisions = all.filter((l) => l.category === "division");
      setCommunityLinks(social.length > 0 ? social : defaultLinks);
      setDivisionLinks(divisions.length > 0 ? divisions : defaultDivisionLinks);
      setLinksLoaded(true);

      if (contentRes.data) {
        for (const item of contentRes.data) {
          if (item.key === "site_title") setSiteTitle(item.value);
          if (item.key === "tagline") setTagline(item.value);
          if (item.key === "main_message") setMainMessage(item.value);
          if (item.key === "why_join_title") setWhyJoinTitle(item.value);
          if (item.key === "why_join_content") setWhyJoinContent(item.value);
          if (item.key === "who_can_join_title") setWhoCanJoinTitle(item.value);
          if (item.key === "who_can_join_intro") setWhoCanJoinIntro(item.value);
          if (item.key === "who_can_join_content") setWhoCanJoinContent(item.value);
        }
      }
    };
    fetchData();
  }, []);

  // Section reveal animations are handled by useRevealOnScroll (CSS-driven,
  // no per-element timers) to avoid forced synchronous layout / thrashing.


  if (underConstruction) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center relative overflow-hidden px-6">
        <Suspense fallback={null}><MatrixRain /></Suspense>
        <div className="relative z-10 text-center max-w-2xl mx-auto">
          <Terminal className="w-16 h-16 mx-auto mb-6 text-primary" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-glow">Page Under Construction</h1>
          <p className="text-muted-foreground mb-8">We're working on something amazing. Join our community to stay updated!</p>
          <div className="flex flex-wrap gap-4 justify-center">
            {communityLinks.slice(0, 4).map((link) => (
              <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" className="black-button">
                {getIconComponent(link.icon, link.icon_url)}
                <span className="ml-2">{link.name.replace("Join our ", "").replace(" community", "").replace(" server", "").replace(" group", "")}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const iconCycle = [Shield, BookOpen, Network];

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <Helmet>
        <title>WhiteHat Hub | Top Cybersecurity & Ethical Hacking Community</title>
        <meta name="description" content="WhiteHat Hub is the top cybersecurity community for ethical hackers, learners & infosec professionals. Join free to learn, connect, and grow in cyber security." />
        <meta name="keywords" content="WhiteHat Hub, whitehathub, white hat hub, whitehat hub community, whitehat hub India, whitehat hub Tamil Nadu, whitehat hub Chennai, whitehat hub Coimbatore, whitehat hub official, whitehat hub website, whitehat hub Navarath, cybersecurity community, ethical hacking community, information security community, learning cybersecurity, cyber professional community, ethical hackers, penetration testing, security research, infosec, bug bounty, OSINT, cyber security learning, Navarath HS, Navarath HS founder, young ethical hacker Tamil Nadu, cybersecurity community India, ethical hacking community Tamil Nadu, best cybersecurity community, top ethical hacking community, number one cybersecurity community India, leading cybersecurity community, cybersecurity forum, hacker community, cybersecurity network, security community online, join cybersecurity community, learn ethical hacking free, cybersecurity mentorship, cyber security enthusiasts, cyber security group, online security community, infosec forum, ethical hacker training, cybersecurity workshop India, CTF community, capture the flag India, red team community, blue team community, purple team, SOC analyst community, cybersecurity students India, hacker meetup India, cybersecurity events Tamil Nadu, online hacking course, free cybersecurity course, cybersecurity webinars, cybersecurity beginners, learn hacking online, cybersecurity certifications, OSCP community, CEH community, eJPT community, CompTIA Security+, hack the box community, tryhackme India, web security, network security, application security, cloud security, mobile security, IoT security, malware analysis, reverse engineering, exploit development, vulnerability assessment, secure coding, devsecops, cyber awareness, cybersecurity career, cybersecurity jobs India, ethical hacking jobs, cybersecurity internship, infosec community India, security researchers, hacker culture, white hat hackers, grey hat hackers, cybersecurity podcast, security blog India, cybersecurity news, ethical hacking blog, cyber defense, threat intelligence, digital forensics, incident response, GRC community, cybersecurity collaboration, hackathon India, cyber college community, cybersecurity students Tamil Nadu, school cybersecurity club, college cybersecurity club, university cybersecurity, cybersecurity scholarships, cybersecurity awareness India, cyber crime prevention, ethical hacking India, cybersecurity training Tamil Nadu, cybersecurity bootcamp, cyber range, kali linux community, parrot os community, burp suite tutorial, nmap tutorial, metasploit community, wireshark community, OWASP top 10, MITRE ATTACK, NIST framework, ISO 27001, cybersecurity roadmap, cybersecurity beginner roadmap, ethical hacking roadmap, cybersecurity mentor, cybersecurity coach, cybersecurity guide, cybersecurity learning path, cyber security skills, cybersecurity portfolio, cybersecurity resume, security operations community, threat hunting community, cybersecurity volunteers, free cyber events, cybersecurity meetup Chennai, cybersecurity meetup Bangalore, cybersecurity meetup Coimbatore, cybersecurity Discord server, cybersecurity Telegram group, cybersecurity WhatsApp group, cybersecurity Linkedin group, cybersecurity YouTube channel India, hacker news India, white hat hacking India, ethical hacking India community, security awareness training, phishing awareness, password security, zero trust community, cyber resilience, cybersecurity for students, cybersecurity for women, women in cybersecurity India, cybersecurity diversity, cybersecurity startup community, cybersecurity entrepreneurs, indie hackers India, cybersecurity research India, academic cybersecurity, cybersecurity hackathons India, ethical hacking course free, learn cybersecurity from scratch, cybersecurity for beginners India, ethical hacking tutorials, hacking tutorials India, cybersecurity skills development, cybersecurity certification roadmap, OSCP preparation India, CEH preparation India, bug bounty India, bug bounty hunters India, HackerOne community, Bugcrowd community, Synack community, Intigriti community, web pentesting, API pentesting, mobile pentesting, Active Directory hacking, AD pentest, OSINT framework, social engineering, phishing simulation, SOC training, blue team training, threat detection, SIEM, SOAR, EDR, XDR, MITRE ATT&amp;CK community, purple teaming, adversary emulation, red team training India, ethical hacker community Chennai, ethical hacker community Bangalore, ethical hacker community Coimbatore, ethical hacker community Madurai, ethical hacker community Kerala, cybersecurity community Karnataka, cybersecurity community Andhra Pradesh, cybersecurity community Telangana, cybersecurity community Mumbai, cybersecurity community Delhi, cybersecurity community Pune, cybersecurity community Hyderabad, cybersecurity community Kolkata, cybersecurity community Asia, cybersecurity community Worldwide, global cybersecurity community, international ethical hacking community, top cybersecurity Discord, best ethical hacking Discord, top cybersecurity Telegram, free cybersecurity mentorship India, cybersecurity 1:1 mentorship, cybersecurity guidance, cyber career guidance, cybersecurity interview prep, cybersecurity interview questions, open source security tools, cybersecurity automation, cloud security engineer, AWS security, Azure security, GCP security, Kubernetes security, container security, supply chain security, AI security, LLM security, prompt injection, AI red teaming, machine learning security, data privacy, GDPR, DPDP India, cyber law India, IT Act India, CERT-In, NCIIPC, dark web monitoring, ransomware defense, ransomware response, cybercrime awareness India, online safety, cyber safety schools, cybersecurity awareness month, cybersecurity CTF Tamil Nadu, jeopardy CTF, attack defense CTF, contribute to cybersecurity open source, OWASP chapter India, null community India, DEF CON groups India, BSides India, c0c0n India, Nullcon, HackInTheBox, BlackHat Asia community" />
        <meta name="news_keywords" content="WhiteHat Hub, cybersecurity community, ethical hacking, Navarath HS, Tamil Nadu cybersecurity, India cybersecurity community, infosec, bug bounty, OSINT, CTF" />
        <meta property="article:tag" content="cybersecurity" />
        <meta property="article:tag" content="ethical hacking" />
        <meta property="article:tag" content="penetration testing" />
        <meta property="article:tag" content="bug bounty" />
        <meta property="article:tag" content="OSINT" />
        <meta property="article:tag" content="information security" />
        <meta property="article:tag" content="WhiteHat Hub" />
        <meta property="article:tag" content="Navarath HS" />
        <meta property="article:tag" content="cybersecurity community India" />
        <meta property="article:section" content="Cybersecurity" />
        <meta name="author" content="WhiteHat Hub" />
        <meta name="publisher" content="WhiteHat Hub" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="bingbot" content="index, follow" />
        <meta name="rating" content="general" />
        <meta name="distribution" content="global" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="3 days" />
        <meta name="theme-color" content="#000000" />
        <meta name="application-name" content="WhiteHat Hub" />
        <meta name="apple-mobile-web-app-title" content="WhiteHat Hub" />
        <link rel="canonical" href="https://whitehat-hub.vercel.app/" />
        <link rel="alternate" hrefLang="en" href="https://whitehat-hub.vercel.app/" />
        <link rel="alternate" hrefLang="x-default" href="https://whitehat-hub.vercel.app/" />
        <link rel="preconnect" href="https://gnxpjbknenzylqkteamj.supabase.co" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://gnxpjbknenzylqkteamj.supabase.co" />
        <link rel="me" href="https://www.linkedin.com/company/whitehat-hub/" />
        <link rel="me" href="https://youtube.com/@whitehathub001" />
        <link rel="me" href="https://discord.gg/FbYp3Tm5B" />
        <link rel="me" href="https://t.me/whitehathub01" />
        <link rel="me" href="https://github.com/whitehathub" />
        <link rel="me" href="https://x.com/whitehathub01" />
        <link rel="me" href="https://twitter.com/whitehathub01" />
        <link rel="me" href="https://www.instagram.com/whitehat.hub/" />
        <link rel="me" href="https://medium.com/@whitehathub" />
        <link rel="me" href="https://dev.to/whitehathub" />
        <link rel="me" href="https://www.reddit.com/user/whitehathub" />
        <link rel="me" href="https://www.facebook.com/whitehathub" />
        <link rel="me" href="https://www.linkedin.com/in/navarath-hs/" />
        <link rel="me" href="https://hacker.ad/groups/WhiteHat.Hub" />
        <meta property="og:see_also" content="https://whitehat-hub.vercel.app/about" />
        <meta property="og:see_also" content="https://whitehat-hub.vercel.app/founder" />
        <meta property="og:see_also" content="https://whitehat-hub.vercel.app/divisions" />
        <meta property="og:see_also" content="https://whitehat-hub.vercel.app/careers" />
        <meta property="og:see_also" content="https://whitehat-hub.vercel.app/past-sessions" />
        <meta property="og:see_also" content="https://whitehat-hub.vercel.app/roadmap" />
        <meta property="og:see_also" content="https://whitehat-hub.vercel.app/partners" />
        <meta property="og:see_also" content="https://whitehat-hub.vercel.app/wall-of-love" />
        <meta name="geo.region" content="IN-TN" />
        <meta name="geo.placename" content="Tamil Nadu, India" />
        <meta name="ICBM" content="11.1271, 78.6569" />
        <meta name="coverage" content="Worldwide" />
        <meta name="audience" content="Cybersecurity learners, ethical hackers, infosec professionals" />
        <meta name="subject" content="Cybersecurity Community, Ethical Hacking, Information Security" />
        <meta name="topic" content="Cybersecurity, Ethical Hacking, Information Security" />
        <meta name="classification" content="Cybersecurity Community" />
        <meta name="copyright" content="WhiteHat Hub" />
        <meta name="designer" content="WhiteHat Hub" />
        <meta name="reply-to" content="whitehathub001@gmail.com" />
        <meta name="owner" content="WhiteHat Hub" />
        <meta name="url" content="https://whitehat-hub.vercel.app/" />
        <meta name="identifier-URL" content="https://whitehat-hub.vercel.app/" />
        <meta name="referrer" content="strict-origin-when-cross-origin" />
        <meta name="format-detection" content="telephone=no" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="WhiteHat Hub" />
        <meta property="og:title" content="WhiteHat Hub | Top Cybersecurity & Ethical Hacking Community" />
        <meta property="og:description" content="Join the top cybersecurity community for ethical hackers, learners, and infosec professionals. Free to join." />
        <meta property="og:url" content="https://whitehat-hub.vercel.app/" />
        <meta property="og:image" content="https://whitehat-hub.vercel.app/whitehat-hub-logo.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content="WhiteHat Hub cybersecurity community logo" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:updated_time" content="2025-06-23T00:00:00+05:30" />
        <meta property="article:published_time" content="2025-06-23T00:00:00+05:30" />
        <meta property="article:modified_time" content="2025-06-23T00:00:00+05:30" />
        <meta property="article:publisher" content="https://whitehat-hub.vercel.app/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@whitehathub" />
        <meta name="twitter:creator" content="@whitehathub" />
        <meta name="twitter:domain" content="whitehat-hub.vercel.app" />
        <meta name="twitter:title" content="WhiteHat Hub | Top Cybersecurity & Ethical Hacking Community" />
        <meta name="twitter:description" content="Join the top cybersecurity community for ethical hackers and security learners." />
        <meta name="twitter:image" content="https://whitehat-hub.vercel.app/whitehat-hub-logo.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://whitehat-hub.vercel.app/#organization",
              "name": "WhiteHat Hub",
              "alternateName": ["WhiteHatHub", "White Hat Hub", "WhiteHat Hub Cybersecurity"],
              "url": "https://whitehat-hub.vercel.app/",
              "logo": {
                "@type": "ImageObject",
                "url": "https://whitehat-hub.vercel.app/whitehat-hub-logo.png",
                "width": 512,
                "height": 512
              },
              "description": "The leading cybersecurity community for ethical hackers, learners, and information security professionals.",
              "foundingDate": "2025-06-23",
              "founder": {
                "@type": "Person",
                "name": "Navarath HS",
                "jobTitle": "Founder, WhiteHat Hub",
                "url": "https://whitehat-hub.vercel.app/founder"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "community support",
                "email": "whitehathub001@gmail.com",
                "availableLanguage": ["English"]
              },
              "location": {
                "@type": "Place",
                "name": "Tamil Nadu, India",
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 11.1271,
                  "longitude": 78.6569
                }
              },
              "sameAs": [
                "https://chat.whatsapp.com/Gj39roBM26YBMndjXw9q1P",
                "https://discord.gg/FbYp3Tm5B",
                "https://t.me/whitehathub01",
                "https://www.linkedin.com/company/whitehat-hub/",
                "https://www.linkedin.com/in/navarath-hs/",
                "https://youtube.com/@whitehathub001",
                "https://github.com/whitehathub",
                "https://x.com/whitehathub01",
                "https://twitter.com/whitehathub01",
                "https://www.instagram.com/whitehat.hub/",
                "https://www.facebook.com/whitehathub",
                "https://medium.com/@whitehathub",
                "https://dev.to/whitehathub",
                "https://hashnode.com/@whitehathub",
                "https://www.reddit.com/user/whitehathub",
                "https://hacker.ad/groups/WhiteHat.Hub",
                "https://www.crunchbase.com/organization/whitehat-hub",
                "https://www.producthunt.com/@whitehathub",
                "https://en.wikipedia.org/wiki/White_hat_(computer_security)"
              ]
            },
            {
              "@type": "WebSite",
              "@id": "https://whitehat-hub.vercel.app/#website",
              "url": "https://whitehat-hub.vercel.app/",
              "name": "WhiteHat Hub",
              "description": "Top cybersecurity community for ethical hackers, learners and infosec professionals.",
              "publisher": { "@id": "https://whitehat-hub.vercel.app/#organization" },
              "inLanguage": "en",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://whitehat-hub.vercel.app/?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            },
            {
              "@type": "WebPage",
              "@id": "https://whitehat-hub.vercel.app/#webpage",
              "url": "https://whitehat-hub.vercel.app/",
              "name": "WhiteHat Hub | Top Cybersecurity & Ethical Hacking Community",
              "isPartOf": { "@id": "https://whitehat-hub.vercel.app/#website" },
              "about": { "@id": "https://whitehat-hub.vercel.app/#organization" },
              "description": "Join WhiteHat Hub – the top cybersecurity community for ethical hackers, learners, and security professionals.",
              "inLanguage": "en",
              "datePublished": "2025-06-23",
              "dateModified": "2025-06-23"
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://whitehat-hub.vercel.app/" },
                { "@type": "ListItem", "position": 2, "name": "About", "item": "https://whitehat-hub.vercel.app/about" },
                { "@type": "ListItem", "position": 3, "name": "Founder", "item": "https://whitehat-hub.vercel.app/founder" },
                { "@type": "ListItem", "position": 4, "name": "Divisions", "item": "https://whitehat-hub.vercel.app/divisions" }
              ]
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is WhiteHat Hub?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "WhiteHat Hub is a leading cybersecurity community for ethical hackers, learners, and information security professionals to learn, connect, and contribute."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Who founded WhiteHat Hub?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "WhiteHat Hub was founded by Navarath HS, a young ethical hacker and cybersecurity professional from Tamil Nadu."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How can I join the WhiteHat Hub cybersecurity community?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "You can join WhiteHat Hub through our WhatsApp, Discord, Telegram, LinkedIn, or YouTube channels listed on the homepage."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is WhiteHat Hub free to join?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, WhiteHat Hub is completely free and open to anyone passionate about cybersecurity, ethical hacking, and information security."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What makes WhiteHat Hub the best cybersecurity community?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "WhiteHat Hub offers free mentorship, networking with industry professionals, hands-on learning, and a supportive environment for cybersecurity enthusiasts of all levels."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is WhiteHat Hub suitable for beginners in cybersecurity?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Absolutely. WhiteHat Hub welcomes beginners, students, and anyone curious about cybersecurity. Our community members help newcomers learn ethical hacking from scratch."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What topics are covered in WhiteHat Hub?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "WhiteHat Hub covers ethical hacking, penetration testing, bug bounty hunting, OSINT, web security, network security, digital forensics, and information security careers."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How is WhiteHat Hub different from other cybersecurity communities?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "WhiteHat Hub is founded by a young ethical hacker from India, focuses on practical learning, offers free mentorship, and builds a tight-knit community across WhatsApp, Discord, and Telegram."
                  }
                }
              ]
            },
            {
              "@type": "EducationalOrganization",
              "name": "WhiteHat Hub",
              "url": "https://whitehat-hub.vercel.app/",
              "description": "Community-driven cybersecurity learning hub for ethical hackers, students, and infosec professionals.",
              "areaServed": "Worldwide",
              "knowsAbout": ["Cybersecurity", "Ethical Hacking", "Penetration Testing", "Bug Bounty", "OSINT", "Information Security", "Web Security", "Network Security", "Digital Forensics", "Malware Analysis"]
            },
            {
              "@type": "ItemList",
              "name": "WhiteHat Hub Community Channels",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "WhatsApp Community", "url": "https://chat.whatsapp.com/Gj39roBM26YBMndjXw9q1P" },
                { "@type": "ListItem", "position": 2, "name": "Discord Server", "url": "https://discord.gg/FbYp3Tm5B" },
                { "@type": "ListItem", "position": 3, "name": "Telegram Group", "url": "https://t.me/whitehathub01" },
                { "@type": "ListItem", "position": 4, "name": "LinkedIn Page", "url": "https://www.linkedin.com/company/whitehat-hub/" },
                { "@type": "ListItem", "position": 5, "name": "YouTube Channel", "url": "https://youtube.com/@whitehathub001" }
              ]
            },
            {
              "@type": "Course",
              "name": "Cybersecurity & Ethical Hacking Community Learning",
              "provider": { "@id": "https://whitehat-hub.vercel.app/#organization" },
              "description": "Free community-driven learning in cybersecurity, ethical hacking, penetration testing, and information security.",
              "courseMode": "online",
              "inLanguage": "en",
              "isAccessibleForFree": true,
              "educationalLevel": "beginner to advanced",
              "about": "Cybersecurity and Ethical Hacking"
            },
            {
              "@type": "Event",
              "name": "WhiteHat Hub Community Events",
              "organizer": { "@id": "https://whitehat-hub.vercel.app/#organization" },
              "description": "Regular cybersecurity events, workshops, and sessions organized by WhiteHat Hub for ethical hackers and security learners.",
              "eventStatus": "https://schema.org/EventScheduled",
              "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
              "isAccessibleForFree": true
            },
            {
              "@type": "HowTo",
              "name": "How to Join WhiteHat Hub Cybersecurity Community",
              "description": "Step-by-step guide to join the WhiteHat Hub cybersecurity community for ethical hackers, learners, and infosec professionals.",
              "step": [
                {
                  "@type": "HowToStep",
                  "position": 1,
                  "name": "Visit the WhiteHat Hub Website",
                  "text": "Go to whitehat-hub.vercel.app to explore the community, resources, and upcoming events.",
                  "url": "https://whitehat-hub.vercel.app/"
                },
                {
                  "@type": "HowToStep",
                  "position": 2,
                  "name": "Choose Your Preferred Platform",
                  "text": "Join via WhatsApp, Discord, Telegram, LinkedIn, or YouTube — whichever platform you use most.",
                  "url": "https://whitehat-hub.vercel.app/"
                },
                {
                  "@type": "HowToStep",
                  "position": 3,
                  "name": "Introduce Yourself",
                  "text": "Share your background, interests in cybersecurity or ethical hacking, and what you hope to learn or contribute.",
                  "url": "https://whitehat-hub.vercel.app/"
                },
                {
                  "@type": "HowToStep",
                  "position": 4,
                  "name": "Start Learning and Contributing",
                  "text": "Participate in discussions, attend events, ask questions, and share knowledge with fellow community members.",
                  "url": "https://whitehat-hub.vercel.app/"
                }
              ],
              "totalTime": "PT5M",
              "estimatedCost": {
                "@type": "MonetaryAmount",
                "currency": "INR",
                "value": "0"
              }
            },
            {
              "@type": "JobPosting",
              "title": "Cybersecurity Community Contributors",
              "description": "Volunteer and non-paid work opportunities for ethical hackers, security researchers, and cybersecurity enthusiasts at WhiteHat Hub.",
              "hiringOrganization": { "@id": "https://whitehat-hub.vercel.app/#organization" },
              "jobLocation": {
                "@type": "Place",
                "address": {
                  "@type": "PostalAddress",
                  "addressCountry": "IN",
                  "addressRegion": "Tamil Nadu"
                }
              },
              "employmentType": "VOLUNTEER",
              "validThrough": "2026-12-31",
              "url": "https://whitehat-hub.vercel.app/careers"
            }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "dateCreated": "2025-06-23",
          "dateModified": "2025-06-23",
          "mainEntity": {
            "@type": "Person",
            "name": "Navarath HS",
            "alternateName": ["Navarath", "WhiteHat Hub Founder"],
            "jobTitle": "Founder, WhiteHat Hub",
            "description": "Young ethical hacker and cybersecurity professional from Tamil Nadu, founder of WhiteHat Hub.",
            "url": "https://whitehat-hub.vercel.app/founder",
            "image": "https://whitehat-hub.vercel.app/whitehat-hub-logo.png",
            "worksFor": { "@id": "https://whitehat-hub.vercel.app/#organization" },
            "knowsAbout": ["Cybersecurity", "Ethical Hacking", "Penetration Testing", "Bug Bounty", "OSINT"],
            "sameAs": [
              "https://www.linkedin.com/in/navarath-hs/",
              "https://x.com/whitehathub01",
              "https://twitter.com/whitehathub01",
              "https://github.com/whitehathub",
              "https://medium.com/@whitehathub",
              "https://dev.to/whitehathub",
              "https://www.instagram.com/whitehat.hub/",
              "https://hacker.ad/groups/WhiteHat.Hub"
            ]
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "WhiteHat Hub Verified Off-Site Profiles & Citations",
          "description": "Authoritative external profiles, citations, and brand mentions of WhiteHat Hub across the web.",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "url": "https://www.linkedin.com/company/whitehat-hub/", "name": "LinkedIn — WhiteHat Hub" },
            { "@type": "ListItem", "position": 2, "url": "https://www.linkedin.com/in/navarath-hs/", "name": "LinkedIn — Founder Navarath HS" },
            { "@type": "ListItem", "position": 3, "url": "https://youtube.com/@whitehathub001", "name": "YouTube Channel" },
            { "@type": "ListItem", "position": 4, "url": "https://github.com/whitehathub", "name": "GitHub Organization" },
            { "@type": "ListItem", "position": 5, "url": "https://medium.com/@whitehathub", "name": "Medium Publication" },
            { "@type": "ListItem", "position": 6, "url": "https://dev.to/whitehathub", "name": "Dev.to Profile" },
            { "@type": "ListItem", "position": 7, "url": "https://hashnode.com/@whitehathub", "name": "Hashnode Blog" },
            { "@type": "ListItem", "position": 8, "url": "https://www.reddit.com/user/whitehathub", "name": "Reddit Profile" },
            { "@type": "ListItem", "position": 9, "url": "https://x.com/whitehathub01", "name": "X (Twitter) Profile" },
            { "@type": "ListItem", "position": 10, "url": "https://www.instagram.com/whitehat.hub/", "name": "Instagram Profile" },
            { "@type": "ListItem", "position": 11, "url": "https://www.facebook.com/whitehathub", "name": "Facebook Page" },
            { "@type": "ListItem", "position": 12, "url": "https://hacker.ad/groups/WhiteHat.Hub", "name": "Hacker Ad Group" },
            { "@type": "ListItem", "position": 13, "url": "https://www.crunchbase.com/organization/whitehat-hub", "name": "Crunchbase Profile" },
            { "@type": "ListItem", "position": 14, "url": "https://www.producthunt.com/@whitehathub", "name": "Product Hunt Profile" },
            { "@type": "ListItem", "position": 15, "url": "https://discord.gg/FbYp3Tm5B", "name": "Discord Server" },
            { "@type": "ListItem", "position": 16, "url": "https://t.me/whitehathub01", "name": "Telegram Group" }
          ]
        })}</script>
      </Helmet>
      <Suspense fallback={null}><MatrixRain /></Suspense>
      <Navigation />

      <main itemScope itemType="https://schema.org/WebPage">
        <meta itemProp="name" content="WhiteHat Hub | Top Cybersecurity & Ethical Hacking Community" />
        <meta itemProp="description" content="Join WhiteHat Hub – the top cybersecurity community for ethical hackers, learners, and security professionals." />
        <link itemProp="url" href="https://whitehat-hub.vercel.app/" />

      <section id="home" className="relative px-6 py-20 md:py-32 scroll-mt-20">
        <div className="container mx-auto max-w-6xl text-center relative z-10">
          <div className="mb-8 flex flex-col items-center section-animate">
            <img
              src={whitehatLogo}
              alt="WhiteHat Hub cybersecurity community logo"
              className="w-20 h-20 md:w-24 md:h-24 rounded-full shadow-lg ring-2 ring-border animate-float-soft hover:scale-105 transition-transform duration-500"
              loading="eager"
              decoding="async"
              width={96}
              height={96}
              {...{ fetchpriority: "high" }}
            />
          </div>

          <div className="mb-8 section-animate" style={{ animationDelay: "100ms" }}>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 tracking-tight" itemProp="name">
              <span className="text-glow">Welcome to</span>
              <br />
              <span className="text-sheen">
                {siteTitle}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground font-medium mt-3" itemProp="alternativeHeadline">
              Cybersecurity Community for Ethical Hackers & Learners
            </p>
          </div>

          <div className="mb-6 max-w-4xl mx-auto section-animate" style={{ animationDelay: "200ms" }}>
            <p className="text-lg md:text-xl text-foreground/90 leading-relaxed" itemProp="description">{mainMessage}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={(communityLinks[0] || defaultLinks[0]).url}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 shimmer-button focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label="Visit WhiteHat Hub community"
              >
                <span>Visit our main community</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </a>
              {divisionLinks.map((d) => (
                <a
                  key={d.id}
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary/40 bg-background/60 backdrop-blur text-foreground font-medium hover:bg-primary/10 hover:border-primary hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label={d.name}
                >
                  <span className="w-4 h-4 text-primary" aria-hidden="true">{getIconComponent(d.icon, d.icon_url)}</span>
                  <span>{d.name}</span>
                  <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>


        </div>
      </section>

      {whoCanJoinCards.length > 0 && (
        <section id="who-can-join" className="relative px-6 py-16 scroll-mt-20 border-t border-border">
          <div className="container mx-auto max-w-5xl relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 reveal-on-scroll">{whoCanJoinTitle}</h2>
            <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto reveal-on-scroll">{whoCanJoinIntro}</p>
            <div className="divide-y divide-border/60 max-w-3xl mx-auto">
              {whoCanJoinCards.map((card, idx) => {
                const icons = [GraduationCap, Shield, Search, Briefcase, Building2, Users];
                const Icon = icons[idx % icons.length];
                return (
                  <div key={`who-${idx}`} className="group flex items-start gap-4 py-5 reveal-on-scroll" style={{ transitionDelay: `${idx * 60}ms` }}>
                    <Icon className="h-5 w-5 text-primary flex-shrink-0 mt-1 icon-tilt" aria-hidden="true" />
                    <div>
                      <h3 className="font-semibold text-base">{card.title}</h3>
                      <p className="text-muted-foreground text-sm mt-1">{card.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section id="why-join" ref={whyJoinRef} className="relative px-6 py-16 scroll-mt-20 border-t border-border">
        <div className="container mx-auto max-w-5xl relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 reveal-on-scroll">{whyJoinTitle}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {whyJoinCards.map((card, idx) => {
              const Icon = iconCycle[idx % iconCycle.length];
              return (
                <div key={`${card.title}-${idx}`} className="group reveal-on-scroll hover-lift rounded-xl p-2" style={{ transitionDelay: `${idx * 90}ms` }}>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-5 h-5 text-primary icon-tilt" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{card.description || card.title}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <TestimonialRotator />




      </main>
      <Footer />
    </div>
  );
};

export default Index;
